import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  CreditCardIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  CalendarIcon,
  BanknotesIcon,
  ChartBarIcon,
  CheckCircleIcon,
  XCircleIcon,
  XMarkIcon,
  LinkIcon,
} from '@heroicons/react/24/outline'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Tabs } from '@/components/ui/Tabs'
import { Badge } from '@/components/ui/Badge'
import { Spinner } from '@/components/ui/Spinner'
import { EmptyState } from '@/components/ui/EmptyState'
import {
  useAccountingStats,
  useQontoStats,
  useQontoConnection,
  useStartSync,
  useQontoBankAccounts,
  useSyncedTransactions,
  useUnpaidAlerts,
} from '@/hooks/useQonto'
import { useQontoConnect } from '@/hooks/useQontoOAuth'
import {
  syncStatusLabels,
  syncStatusColors,
  alertLevelLabels,
  alertLevelColors,
} from '@/types/qonto'

export default function ComptabilitePage() {
  const [activeTab, setActiveTab] = useState('overview')
  const [searchParams, setSearchParams] = useSearchParams()
  const { data: stats, isLoading: statsLoading } = useAccountingStats()
  const { data: qontoStats } = useQontoStats()
  const { data: isConnected, isLoading: connectionLoading } = useQontoConnection()
  const { data: accounts } = useQontoBankAccounts()
  const startSync = useStartSync()
  const { connect, isLoading: isConnecting } = useQontoConnect()

  // Retour du callback OAuth Qonto (?qonto_connected=true ou ?qonto_error=...)
  const qontoConnectedParam = searchParams.get('qonto_connected')
  const qontoErrorParam = searchParams.get('qonto_error')

  const dismissOAuthBanner = () => {
    const next = new URLSearchParams(searchParams)
    next.delete('qonto_connected')
    next.delete('qonto_error')
    setSearchParams(next, { replace: true })
  }

  const handleSync = async () => {
    if (!accounts || accounts.length === 0) return

    try {
      await startSync.mutateAsync({
        accountSlug: accounts[0].slug,
      })
    } catch (error) {
      console.error('Sync failed:', error)
    }
  }

  const tabs = [
    { id: 'overview', label: 'Vue d\'ensemble', icon: ChartBarIcon },
    { id: 'transactions', label: 'Transactions', icon: CreditCardIcon },
    { id: 'unpaid', label: 'Impayes', icon: ExclamationTriangleIcon },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Comptabilite</h1>
          <p className="mt-1 text-sm text-gray-500">
            Synchronisation bancaire, detection d'impayes et paiements recurrents
          </p>
        </div>
        <div className="flex gap-3">
          <Link to="/comptabilite/recurring">
            <Button variant="outline" icon={CalendarIcon}>
              Paiements recurrents
            </Button>
          </Link>
          {!connectionLoading && !isConnected && (
            <Button
              variant="primary"
              icon={LinkIcon}
              onClick={() => connect()}
              loading={isConnecting}
            >
              Connecter Qonto
            </Button>
          )}
          {isConnected && (
            <Button
              variant="primary"
              icon={ArrowPathIcon}
              onClick={handleSync}
              loading={startSync.isPending}
            >
              Synchroniser
            </Button>
          )}
        </div>
      </div>

      {/* Resultat du retour OAuth Qonto */}
      {qontoConnectedParam && (
        <div className="flex items-center justify-between rounded-lg bg-mint-50 border border-mint-200 px-4 py-3 text-mint-700">
          <div className="flex items-center gap-2">
            <CheckCircleIcon className="w-5 h-5 shrink-0" />
            <p className="text-sm font-medium">Qonto connecte avec succes.</p>
          </div>
          <button onClick={dismissOAuthBanner} aria-label="Fermer">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
      )}
      {qontoErrorParam && (
        <div className="flex items-center justify-between rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-red-700">
          <div className="flex items-center gap-2">
            <XCircleIcon className="w-5 h-5 shrink-0" />
            <p className="text-sm font-medium">
              Echec de la connexion Qonto : {qontoErrorParam}
            </p>
          </div>
          <button onClick={dismissOAuthBanner} aria-label="Fermer">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Stats Cards */}
      {statsLoading ? (
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : stats ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Unpaid Total */}
          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Impayes</p>
                <p className="mt-2 text-3xl font-bold text-gray-900">
                  {stats.unpaidTotal.toFixed(2)} €
                </p>
                <p className="mt-1 text-sm text-gray-500">
                  {stats.unpaidCount} facture{stats.unpaidCount > 1 ? 's' : ''}
                </p>
              </div>
              <div className="p-3 bg-gold-100 rounded-lg">
                <BanknotesIcon className="w-6 h-6 text-gold-600" />
              </div>
            </div>
          </Card>

          {/* Overdue */}
          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">En retard</p>
                <p className="mt-2 text-3xl font-bold text-red-600">
                  {stats.overdueTotal.toFixed(2)} €
                </p>
                <p className="mt-1 text-sm text-gray-500">
                  {stats.overdueCount} facture{stats.overdueCount > 1 ? 's' : ''}
                </p>
              </div>
              <div className="p-3 bg-red-100 rounded-lg">
                <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </Card>

          {/* Recurring Revenue */}
          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Revenu mensuel (MRR)</p>
                <p className="mt-2 text-3xl font-bold text-mint-600">
                  {stats.recurringMonthlyRevenue.toFixed(2)} €
                </p>
                <p className="mt-1 text-sm text-gray-500">
                  {stats.activeContracts} contrat{stats.activeContracts > 1 ? 's' : ''}
                </p>
              </div>
              <div className="p-3 bg-mint-100 rounded-lg">
                <CalendarIcon className="w-6 h-6 text-mint-600" />
              </div>
            </div>
          </Card>

          {/* Alerts */}
          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Alertes actives</p>
                <div className="mt-2 space-y-1">
                  <div className="flex items-center gap-2">
                    <Badge variant="warning" size="sm">
                      {stats.alertsCount.warning}
                    </Badge>
                    <span className="text-xs text-gray-600">Attention</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="danger" size="sm">
                      {stats.alertsCount.danger}
                    </Badge>
                    <span className="text-xs text-gray-600">Urgent</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="error" size="sm">
                      {stats.alertsCount.critical}
                    </Badge>
                    <span className="text-xs text-gray-600">Critique</span>
                  </div>
                </div>
              </div>
              <div className="p-3 bg-orange-100 rounded-lg">
                <ExclamationTriangleIcon className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </Card>
        </div>
      ) : null}

      {/* Qonto Connection Status */}
      {isConnected === false && (
        <Card>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ExclamationTriangleIcon className="w-6 h-6 text-gold-600" />
              <div>
                <h3 className="font-medium text-gray-900">Qonto non configure</h3>
                <p className="text-sm text-gray-500">
                  Configurez vos identifiants Qonto pour synchroniser les transactions
                </p>
              </div>
            </div>
            <Link to="/settings">
              <Button variant="outline">Configurer</Button>
            </Link>
          </div>
        </Card>
      )}

      {/* Qonto Stats */}
      {qontoStats && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Synchronisation Qonto
            </h3>
            {qontoStats.lastSyncAt && (
              <p className="text-sm text-gray-500">
                Derniere sync: {new Date(qontoStats.lastSyncAt).toLocaleString('fr-FR')}
              </p>
            )}
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-2xl font-bold text-gray-900">
                {qontoStats.syncedTransactions}
              </p>
              <p className="text-sm text-gray-600">Synchronisees</p>
            </div>
            <div className="text-center p-4 bg-mint-50 rounded-lg">
              <p className="text-2xl font-bold text-mint-600">
                {qontoStats.matchedTransactions}
              </p>
              <p className="text-sm text-gray-600">Rapprochees</p>
            </div>
            <div className="text-center p-4 bg-gold-50 rounded-lg">
              <p className="text-2xl font-bold text-gold-600">
                {qontoStats.pendingTransactions}
              </p>
              <p className="text-sm text-gray-600">En attente</p>
            </div>
          </div>
        </Card>
      )}

      {/* Tabs */}
      <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

      {/* Tab Content */}
      {activeTab === 'overview' && <OverviewTab />}
      {activeTab === 'transactions' && <TransactionsTab />}
      {activeTab === 'unpaid' && <UnpaidTab />}
    </div>
  )
}

function OverviewTab() {
  return (
    <Card>
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Vue d'ensemble</h3>
      <div className="prose prose-sm max-w-none">
        <p>
          Cette section vous permet de gerer votre comptabilite avec les fonctionnalites suivantes :
        </p>
        <ul>
          <li>
            <strong>Synchronisation Qonto :</strong> Synchronisez automatiquement vos transactions
            bancaires depuis votre compte Qonto
          </li>
          <li>
            <strong>Rapprochement automatique :</strong> Les paiements sont automatiquement
            rapproches des factures correspondantes
          </li>
          <li>
            <strong>Detection d'impayes :</strong> Recevez des alertes pour les factures impayees
            et gerez les relances
          </li>
          <li>
            <strong>Paiements recurrents :</strong> Gerez vos contrats d'abonnement et generez
            automatiquement les factures
          </li>
        </ul>
      </div>
    </Card>
  )
}

function TransactionsTab() {
  const [page, setPage] = useState(1)
  const { data, isLoading } = useSyncedTransactions({ page, limit: 20 })

  if (isLoading) {
    return (
      <Card>
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      </Card>
    )
  }

  if (!data || data.transactions.length === 0) {
    return (
      <Card>
        <EmptyState
          icon={CreditCardIcon}
          title="Aucune transaction"
          description="Les transactions synchronisees apparaitront ici"
        />
      </Card>
    )
  }

  return (
    <Card>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead>
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Libelle
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Montant
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Statut
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Facture
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.transactions.map((tx) => (
              <tr key={tx.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {new Date(tx.emitted_at).toLocaleDateString('fr-FR')}
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">
                  <div>{tx.label}</div>
                  {tx.counterparty && (
                    <div className="text-xs text-gray-500">{tx.counterparty}</div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <span
                    className={`font-medium ${
                      tx.side === 'credit' ? 'text-mint-600' : 'text-gray-900'
                    }`}
                  >
                    {tx.side === 'credit' ? '+' : '-'}
                    {(tx.amount_cents / 100).toFixed(2)} €
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <Badge
                    variant={tx.sync_status === 'matched' ? 'success' : 'default'}
                    className={`${syncStatusColors[tx.sync_status].bg} ${syncStatusColors[tx.sync_status].text}`}
                  >
                    {syncStatusLabels[tx.sync_status]}
                  </Badge>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {tx.matched_invoice_id ? (
                    <Link
                      to={`/invoices/${tx.matched_invoice_id}`}
                      className="text-mint-600 hover:text-mint-700"
                    >
                      Voir facture
                    </Link>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  )
}

function UnpaidTab() {
  const [page, setPage] = useState(1)
  const { data, isLoading } = useUnpaidAlerts({ page, limit: 20 })

  if (isLoading) {
    return (
      <Card>
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      </Card>
    )
  }

  if (!data || data.alerts.length === 0) {
    return (
      <Card>
        <EmptyState
          icon={ExclamationTriangleIcon}
          title="Aucun impaye"
          description="Les alertes d'impayes apparaitront ici"
        />
      </Card>
    )
  }

  return (
    <Card>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead>
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Niveau
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Facture
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Client
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Montant
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Retard
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.alerts.map((alert) => (
              <tr key={alert.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <Badge
                    variant={alert.alert_level === 'critical' ? 'error' : alert.alert_level === 'danger' ? 'danger' : 'warning'}
                    className={`${alertLevelColors[alert.alert_level].bg} ${alertLevelColors[alert.alert_level].text}`}
                  >
                    {alertLevelLabels[alert.alert_level]}
                  </Badge>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  {alert.invoice && (
                    <Link
                      to={`/invoices/${alert.invoice.id}`}
                      className="text-mint-600 hover:text-mint-700 font-medium"
                    >
                      {alert.invoice.numero}
                    </Link>
                  )}
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">
                  {alert.invoice?.client && (
                    <div>
                      <div className="font-medium">{alert.invoice.client.full_name}</div>
                      {alert.invoice.client.company && (
                        <div className="text-xs text-gray-500">
                          {alert.invoice.client.company}
                        </div>
                      )}
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {(alert.amount_due_cents / 100).toFixed(2)} €
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600">
                  {alert.days_overdue} jour{alert.days_overdue > 1 ? 's' : ''}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <Button variant="outline" size="sm">
                    Relancer
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  )
}
